import React, { useCallback, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import useVoucherValues from 'views/Conversation/Sider/VoucherDrawer/useVoucherValues';
import CouponModal, { CouponModalRef } from './CouponModal';

export interface CouponPickerContextValue {
  openCreate: () => void;
  openEdit: (voucher: Conversation.Voucher.Vo) => void;
  openCopy: (voucher: Conversation.Voucher.Vo) => void;
}

const CouponPickerContext = React.createContext<CouponPickerContextValue | null>(null);

export function useCouponPickerContext() {
  const ctx = React.useContext(CouponPickerContext);
  return ctx;
}

export interface CouponPickerProviderProps {
  conversation: Conversation.Conversation;
  afterSubmit?: () => void;
  children: React.ReactNode;
}

export function CouponPickerProvider(props: CouponPickerProviderProps) {
  const { conversation, afterSubmit, children } = props;
  const { t } = useTranslate();
  const { storeId } = conversation;
  const { clearVoucherFormCache } = useVoucherValues();

  const modalRef = useRef<CouponModalRef>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalConfirmText, setModalConfirmText] = useState('');

  const openCreate = useCallback(() => {
    modalRef.current?.form.resetFields();
    modalRef.current?.setStatus(undefined);
    setModalTitle(t('chatWorkspace.couponForm.title.create'));
    setModalConfirmText(t('common.action.confirm'));
    setModalVisible(true);
  }, [t]);

  const openEdit = useCallback(
    (voucher: Conversation.Voucher.Vo) => {
      modalRef.current?.setFormValues(voucher);
      modalRef.current?.setStatus(voucher.voucherStatus as Conversation.Voucher.Status);
      setModalTitle(t('chatWorkspace.couponForm.title.edit'));
      setModalConfirmText(t('chatWorkspace.couponForm.button.confirmEdit'));
      setModalVisible(true);
    },
    [t],
  );

  const openCopy = useCallback(
    (voucher: Conversation.Voucher.Vo) => {
      const {
        voucherCode,
        internalVoucherId,
        externalVoucherId,
        startTime,
        endTime,
        displayCollectStartTime,
        ...otherProps
      } = voucher;
      modalRef.current?.setFormValues(otherProps as Conversation.Voucher.Vo);
      modalRef.current?.setStatus(undefined);
      clearVoucherFormCache(storeId);
      setModalTitle(t('chatWorkspace.couponForm.title.create'));
      setModalConfirmText(t('common.action.confirm'));
      setModalVisible(true);
    },
    [t, storeId, clearVoucherFormCache],
  );

  const contextValue: CouponPickerContextValue = {
    openCreate,
    openEdit,
    openCopy,
  };

  const handleCancel = useCallback(() => {
    setModalVisible(false);
  }, []);

  const handleAfterSubmit = useCallback(() => {
    setModalVisible(false);
    afterSubmit?.();
  }, [afterSubmit]);

  return (
    <CouponPickerContext.Provider value={contextValue}>
      {children}
      <CouponModal
        ref={modalRef}
        conversation={conversation}
        visible={modalVisible}
        title={modalTitle}
        confirmText={modalConfirmText}
        onCancel={handleCancel}
        afterSubmit={handleAfterSubmit}
      />
    </CouponPickerContext.Provider>
  );
}
